/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { jsx, css } from '@emotion/react';
import { FormattedMessage, useIntl } from 'components/i18n';
import PageHeader from '@atlaskit/page-header';
import Button, { ButtonGroup } from '@atlaskit/button';
import Spinner from '@atlaskit/spinner';
import { usePaginatedQuery, queryCache } from 'react-query';
import { DatePicker } from '../../components/date-picker';
import TablePage from '../../components/page/TablePage';
import StatusFilter from '../sale/components/StatusFilter';
import useApiQuery from '../../hooks/useApiQuery';
import CompanyLink from '../auth/CompanyLink';
import { fetchInvoices } from '../sale/SalesPage';
import PurchasesTable from './components/PurchasesTable';
import SearchBar from '../../components/SearchBar';

const PurchasesPage = () => {
  const intl = useIntl();
  const { queryParams, debouncedUpdateQueryParams, updateQueryParams, handleChangeQueryParam } = useApiQuery();
  const { page } = queryParams;
  const {search: searchQ, dateTo, dateFrom, status} = queryParams;
  const [search, setSearch] = useState<string>(searchQ);

  const {
    resolvedData,
    latestData,
    isFetching,
    isLoading,
  } = usePaginatedQuery(
    ['purchases', queryParams],
    fetchInvoices
  );

  // Prefetch the next page!
  React.useEffect(() => {
    if (page) {
      queryCache.prefetchQuery(
        ['purchases', {...queryParams, page: queryParams.page + 1}],
        fetchInvoices
      );
    }
  }, [resolvedData]);

  const handleSearch = React.useCallback(
    (value) => {
      setSearch(value);
      debouncedUpdateQueryParams({ search: value, page: 1});
    },
    [setSearch, debouncedUpdateQueryParams]
  );
  const resetSearch = React.useCallback(
    () => {
      setSearch('');
      debouncedUpdateQueryParams({ search: '', page: 1});
    },
    [setSearch, debouncedUpdateQueryParams]
  );

  const barContent = React.useMemo(() => {
    const searchLabel = intl.formatMessage({
      id: 'sale.PurchasesPage.search',
      defaultMessage: 'Search',
      description: 'Search placeholder',
    });

    const dateFromPlaceholder = intl.formatMessage({
      id: 'sale.SalesPage.dateFromPlaceholder',
      defaultMessage: 'From',
    });
    const dateToPlaceholder = intl.formatMessage({
      id: 'sale.SalesPage.dateToPlaceholder',
      defaultMessage: 'To',
    });

    const itemsContainer = css`
      display: flex;
      align-items: center;
      margin-top: 1rem;
      margin-bottom: 1rem;
      && > div:not(:first-of-type) {
        margin-left: 8px;
      }
    `;

    return (
      <div>
        <div css={itemsContainer}>
          <div css={css`flex: 0 0 300px;`}>
            <SearchBar
              placeholder={searchLabel}
              value={search}
              onChange={handleSearch}
              onReset={resetSearch}
            />
          </div>
          <div css={css`flex: 0 0 100px;`}>
            <StatusFilter value={status} onChange={handleChangeQueryParam('status')} options={['unpaid', 'paid','partiallypaid']} />
          </div>
          <div css={css`flex: 0 0 135px;`}>
            <DatePicker
              defaultValue={dateFrom!}
              onChange={handleChangeQueryParam('dateFrom')}
              placeholder={dateFromPlaceholder}
              spacing="compact"
            />
          </div>
          <div css={css`flex: 0 0 135px;`}>
            <DatePicker
              defaultValue={dateTo!}
              onChange={handleChangeQueryParam('dateTo')}
              placeholder={dateToPlaceholder}
              spacing="compact"
            />
          </div>
          <div>{isFetching && <Spinner/>}</div>
        </div>
      </div>
    );
  }, [intl, status, isFetching, search, searchQ]);

  const actionsContent = React.useMemo(
    () => (
      <ButtonGroup>
        <CompanyLink to="/inbox">
          <Button appearance="default" onClick={() => null}>
            <FormattedMessage
              id="purchase.PurchasesPage.inbox"
              defaultMessage="Inbox"
            />
          </Button>
        </CompanyLink>
      </ButtonGroup>
    ),
    []
  );

  const header = (
    <PageHeader bottomBar={barContent} actions={actionsContent}>
      <FormattedMessage id="purchase.PurchasesPage.title" defaultMessage="Purchases" />
    </PageHeader>
  );

  return (
    <TablePage header={header}>
        <PurchasesTable
          data={latestData?.data}
          pageCount={latestData?.meta?.totalPageCount}
          updateQueryParams={updateQueryParams}
          isLoading={isLoading}
          {...queryParams}
        />
    </TablePage>
  );
};

export default PurchasesPage;
